/**
 * Tabs component
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { HelpArticleIcon } from '../HelpCenter/HelpArticleIcon';

export interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  badge?: string | number;
  /** Optional hover description (native title attribute) for accessibility and guidance */
  title?: string;
  /** Opens Help Center to this article from an inline info icon on the tab label. */
  helpArticleId?: string;
}

export interface TabsProps {
  tabs: Tab[];
  /** Initial tab when uncontrolled. Ignored when activeTab is provided. */
  defaultTab?: string;
  /** When provided, component is controlled: display this tab and call onChange on click. */
  activeTab?: string;
  onChange?: (tabId: string) => void;
}

export function Tabs({ tabs, defaultTab, activeTab: controlledActiveTab, onChange }: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (tabId: string) => {
    if (!isControlled) setInternalActiveTab(tabId);
    onChange?.(tabId);
  };

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  return (
    <div className="bg-theme-paper">
      <div className="border-b border-theme-border bg-theme-paper">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              title={tab.title}
              onClick={() => handleTabChange(tab.id)}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border'
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                {tab.helpArticleId && (
                  <HelpArticleIcon
                    articleId={tab.helpArticleId}
                    label={`Help: ${tab.label}`}
                    className="opacity-70 hover:opacity-100"
                  />
                )}
              </span>
              {tab.badge !== undefined && (
                <span className="ml-2 bg-theme-surface-hover text-theme-text py-0.5 px-2 rounded-full text-xs">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div className="mt-4">{activeTabContent}</div>
    </div>
  );
}


